import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Plus, Trash2, Save, Upload, Loader2, ChevronDown, ChevronRight,
  Image as ImageIcon, RefreshCw, AlertCircle, GripVertical, ArrowUp, ArrowDown,
} from 'lucide-react';
import { API, ADMIN_TOKEN, resolveImg } from './adminConfig';

const SECTIONS = [
  { key: 'urunler', label: 'Landing · Ürün Grupları' },
  { key: 'sanver', label: 'Şanver B2B · Helva Çeşitleri' },
];

const emptyCat = (section) => ({
  section, name: '', tag: '', subtitle: '', description: '',
  image_url: '', accent: '#B08834',
});

const emptySub = (category_id) => ({
  category_id, name: '', description: '', image_url: '',
});

export default function AdminCMS() {
  const [section, setSection] = useState('urunler');
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/horeca/content`, { params: { section } });
      setCats(res.data?.[section] || []);
    } catch {
      setError('İçerik yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [section]);

  const persistOrder = async (type, ids) => {
    try {
      await axios.post(
        `${API}/horeca/admin/reorder`,
        { type, ids },
        { params: { token: ADMIN_TOKEN } },
      );
    } catch {
      // sessizce geri yükle
      load();
    }
  };

  const onCatDragStart = (id) => setDragId(id);
  const onCatDragOver = (e, overId) => {
    e.preventDefault();
    if (overId !== dragOverId) setDragOverId(overId);
  };
  const onCatDrop = (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) {
      setDragId(null); setDragOverId(null); return;
    }
    const src = cats.findIndex((c) => c.id === dragId);
    const dst = cats.findIndex((c) => c.id === targetId);
    if (src < 0 || dst < 0) { setDragId(null); setDragOverId(null); return; }
    const next = [...cats];
    const [moved] = next.splice(src, 1);
    next.splice(dst, 0, moved);
    setCats(next);
    setDragId(null); setDragOverId(null);
    persistOrder('category', next.map((c) => c.id));
  };

  const reorderSubs = (catId, nextSubs) => {
    setCats((prev) => prev.map((c) => c.id === catId ? { ...c, subcategories: nextSubs } : c));
    persistOrder('subcategory', nextSubs.map((s) => s.id));
  };

  const moveCat = (id, dir) => {
    const idx = cats.findIndex((c) => c.id === id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= cats.length) return;
    const next = [...cats];
    [next[idx], next[target]] = [next[target], next[idx]];
    setCats(next);
    persistOrder('category', next.map((c) => c.id));
  };

  const moveSub = (catId, subId, dir) => {
    const cat = cats.find((c) => c.id === catId);
    if (!cat) return;
    const subs = cat.subcategories || [];
    const idx = subs.findIndex((s) => s.id === subId);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= subs.length) return;
    const next = [...subs];
    [next[idx], next[target]] = [next[target], next[idx]];
    reorderSubs(catId, next);
  };

  const addCategory = async () => {
    const payload = { ...emptyCat(section), name: 'Yeni Kategori' };
    const res = await axios.post(`${API}/horeca/admin/categories`, payload, {
      params: { token: ADMIN_TOKEN },
    });
    setCats((prev) => [...prev, res.data]);
    setOpenId(res.data.id);
  };

  const updateCategory = (id, patch) => {
    setCats((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const saveCategory = async (cat) => {
    const { id, section: _s, subcategories: _sub, ...rest } = cat;
    await axios.put(`${API}/horeca/admin/categories/${id}`, rest, {
      params: { token: ADMIN_TOKEN },
    });
  };

  const removeCategory = async (id) => {
    if (!window.confirm('Bu kategori ve alt ürünleri silinsin mi?')) return;
    await axios.delete(`${API}/horeca/admin/categories/${id}`, {
      params: { token: ADMIN_TOKEN },
    });
    setCats((prev) => prev.filter((c) => c.id !== id));
  };

  const addSub = async (categoryId) => {
    const res = await axios.post(
      `${API}/horeca/admin/subcategories`,
      { ...emptySub(categoryId), name: 'Yeni Alt Ürün' },
      { params: { token: ADMIN_TOKEN } },
    );
    setCats((prev) => prev.map((c) =>
      c.id === categoryId
        ? { ...c, subcategories: [...(c.subcategories || []), res.data] }
        : c
    ));
  };

  const updateSub = (catId, subId, patch) => {
    setCats((prev) => prev.map((c) =>
      c.id !== catId ? c : {
        ...c,
        subcategories: (c.subcategories || []).map((s) =>
          s.id === subId ? { ...s, ...patch } : s
        ),
      }
    ));
  };

  const saveSub = async (sub) => {
    const { id, category_id: _c, ...rest } = sub;
    await axios.put(`${API}/horeca/admin/subcategories/${id}`, rest, {
      params: { token: ADMIN_TOKEN },
    });
  };

  const removeSub = async (catId, subId) => {
    if (!window.confirm('Bu alt ürün silinsin mi?')) return;
    await axios.delete(`${API}/horeca/admin/subcategories/${subId}`, {
      params: { token: ADMIN_TOKEN },
    });
    setCats((prev) => prev.map((c) =>
      c.id !== catId ? c : {
        ...c,
        subcategories: (c.subcategories || []).filter((s) => s.id !== subId),
      }
    ));
  };

  return (
    <div className="space-y-6" data-testid="admin-cms-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex bg-white border border-[#1F1F1F]/15">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              data-testid={`cms-section-${s.key}`}
              className={`px-4 py-2.5 text-xs uppercase tracking-[0.18em] transition-colors ${
                section === s.key
                  ? 'bg-[#1F1F1F] text-[#FBF7EE]'
                  : 'text-[#5A5145] hover:text-[#1F1F1F]'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          className="text-xs uppercase tracking-[0.2em] text-[#5A5145] hover:text-[#B08834] flex items-center gap-2"
          data-testid="cms-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Yenile
        </button>
        <div className="ml-auto" />
        <button
          onClick={addCategory}
          className="inline-flex items-center gap-2 bg-[#B08834] hover:bg-[#8f6d2a] text-[#1F1F1F] px-4 py-2 text-xs tracking-[0.18em]"
          data-testid="cms-add-category-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          Kategori Ekle
        </button>
      </div>

      <div className="text-xs text-[#5A5145] font-body flex items-center gap-2 -mt-2">
        <GripVertical className="w-3.5 h-3.5 text-[#B08834]" />
        Kart ya da alt ürünlerin başındaki tutamağı sürükleyerek veya ok butonlarıyla sırayı değiştirebilirsiniz — landing sayfası bu sırayı esas alır.
      </div>

      {loading && (
        <div className="py-24 flex flex-col items-center text-[#5A5145]">
          <Loader2 className="w-6 h-6 animate-spin mb-3" />
          Yükleniyor…
        </div>
      )}

      {!loading && error && (
        <div className="py-12 flex flex-col items-center text-[#B03A2E]" data-testid="cms-error">
          <AlertCircle className="w-6 h-6 mb-2" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {!loading && !error && cats.length === 0 && (
        <div className="py-24 text-center text-[#5A5145] font-body">
          Henüz kategori yok. Yukarıdan yeni bir kategori ekleyin.
        </div>
      )}

      {!loading && cats.map((c, idx) => (
        <CategoryCard
          key={c.id}
          category={c}
          canMoveUp={idx > 0}
          canMoveDown={idx < cats.length - 1}
          onMoveUp={() => moveCat(c.id, -1)}
          onMoveDown={() => moveCat(c.id, 1)}
          open={openId === c.id}
          isDragging={dragId === c.id}
          isDragOver={dragOverId === c.id && dragId !== c.id}
          onDragStart={() => onCatDragStart(c.id)}
          onDragOver={(e) => onCatDragOver(e, c.id)}
          onDrop={(e) => onCatDrop(e, c.id)}
          onDragEnd={() => { setDragId(null); setDragOverId(null); }}
          onToggle={() => setOpenId(openId === c.id ? null : c.id)}
          onChange={(patch) => updateCategory(c.id, patch)}
          onSave={() => saveCategory(c)}
          onDelete={() => removeCategory(c.id)}
          onAddSub={() => addSub(c.id)}
          onSubChange={(sid, patch) => updateSub(c.id, sid, patch)}
          onSubSave={(sub) => saveSub(sub)}
          onSubDelete={(sid) => removeSub(c.id, sid)}
          onSubsReorder={(nextSubs) => reorderSubs(c.id, nextSubs)}
          onSubMove={(sid, dir) => moveSub(c.id, sid, dir)}
        />
      ))}
    </div>
  );
}

function CategoryCard({
  category, open, isDragging, isDragOver,
  canMoveUp, canMoveDown, onMoveUp, onMoveDown,
  onDragStart, onDragOver, onDrop, onDragEnd,
  onToggle, onChange, onSave, onDelete,
  onAddSub, onSubChange, onSubSave, onSubDelete, onSubsReorder, onSubMove,
}) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [subDragId, setSubDragId] = useState(null);
  const [subDragOverId, setSubDragOverId] = useState(null);

  const doSave = async () => {
    setSaving(true);
    try {
      await onSave();
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } finally {
      setSaving(false);
    }
  };

  const handleSubDrop = (targetId) => {
    if (!subDragId || subDragId === targetId) {
      setSubDragId(null); setSubDragOverId(null); return;
    }
    const subs = category.subcategories || [];
    const src = subs.findIndex((s) => s.id === subDragId);
    const dst = subs.findIndex((s) => s.id === targetId);
    if (src < 0 || dst < 0) { setSubDragId(null); setSubDragOverId(null); return; }
    const next = [...subs];
    const [moved] = next.splice(src, 1);
    next.splice(dst, 0, moved);
    setSubDragId(null); setSubDragOverId(null);
    onSubsReorder(next);
  };

  return (
    <div
      className={`bg-white border transition-all ${
        isDragOver ? 'border-[#B08834] border-2' : 'border-[#1F1F1F]/12'
      } ${isDragging ? 'opacity-40' : ''}`}
      data-testid={`cms-cat-${category.id}`}
      draggable={!open}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#1F1F1F]/8">
        <span
          className="cursor-grab active:cursor-grabbing p-1 text-[#5A5145]/50 hover:text-[#1F1F1F] hidden md:inline-flex"
          title="Sürükleyerek sırala"
          data-testid={`cms-cat-drag-${category.id}`}
        >
          <GripVertical className="w-4 h-4" />
        </span>
        <div className="flex flex-col">
          <button
            onClick={onMoveUp}
            disabled={!canMoveUp}
            className="text-[#5A5145] disabled:opacity-30 p-0.5 hover:text-[#B08834]"
            title="Yukarı taşı"
            data-testid={`cms-cat-up-${category.id}`}
          >
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onMoveDown}
            disabled={!canMoveDown}
            className="text-[#5A5145] disabled:opacity-30 p-0.5 hover:text-[#B08834]"
            title="Aşağı taşı"
            data-testid={`cms-cat-down-${category.id}`}
          >
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
        </div>
        <button
          onClick={onToggle}
          className="p-1 text-[#5A5145] hover:text-[#1F1F1F]"
          data-testid={`cms-cat-toggle-${category.id}`}
        >
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="w-10 h-10 bg-[#F3EEE2] flex items-center justify-center overflow-hidden shrink-0">
          {category.image_url ? (
            <img src={resolveImg(category.image_url)} alt="" className="w-full h-full object-contain" />
          ) : (
            <ImageIcon className="w-4 h-4 text-[#5A5145]/50" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[#1F1F1F] truncate">{category.name || 'İsimsiz'}</div>
          <div className="text-xs text-[#5A5145] truncate">
            {(category.subcategories || []).length} alt ürün · {category.tag || '—'}
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-xs text-[#B03A2E] hover:text-[#8A2E22] uppercase tracking-[0.18em] flex items-center gap-1"
          data-testid={`cms-cat-delete-${category.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
          Sil
        </button>
      </div>

      {open && (
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Ad" value={category.name} onChange={(v) => onChange({ name: v })} testId={`cms-cat-name-${category.id}`} />
            <Field label="Etiket (Tag)" value={category.tag || ''} onChange={(v) => onChange({ tag: v })} testId={`cms-cat-tag-${category.id}`} />
            <Field label="Alt Başlık" value={category.subtitle || ''} onChange={(v) => onChange({ subtitle: v })} testId={`cms-cat-subtitle-${category.id}`} />
            <ColorField label="Vurgu Rengi" value={category.accent || '#B08834'} onChange={(v) => onChange({ accent: v })} testId={`cms-cat-accent-${category.id}`} />
          </div>
          <Field
            label="Açıklama"
            value={category.description || ''}
            onChange={(v) => onChange({ description: v })}
            multiline
            testId={`cms-cat-description-${category.id}`}
          />
          <ImageField
            label="Ana Görsel"
            value={category.image_url || ''}
            onChange={(v) => onChange({ image_url: v })}
            testId={`cms-cat-image-${category.id}`}
          />

          <div className="flex items-center gap-3 pt-3 border-t border-[#1F1F1F]/10">
            <button
              onClick={doSave}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-[#1F1F1F] hover:bg-[#B03A2E] text-[#FBF7EE] px-5 py-2.5 text-xs tracking-[0.2em] disabled:opacity-60"
              data-testid={`cms-cat-save-${category.id}`}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {saved ? 'Kaydedildi' : 'Kaydet'}
            </button>
            <div className="ml-auto" />
            <button
              onClick={onAddSub}
              className="inline-flex items-center gap-2 bg-[#B08834]/15 text-[#1F1F1F] hover:bg-[#B08834]/25 px-4 py-2 text-xs tracking-[0.18em]"
              data-testid={`cms-cat-add-sub-${category.id}`}
            >
              <Plus className="w-3.5 h-3.5" />
              Alt Ürün Ekle
            </button>
          </div>

          <div className="space-y-3">
            {(category.subcategories || []).map((s, sIdx, arr) => (
              <SubRow
                key={s.id}
                sub={s}
                canMoveUp={sIdx > 0}
                canMoveDown={sIdx < arr.length - 1}
                onMoveUp={() => onSubMove(s.id, -1)}
                onMoveDown={() => onSubMove(s.id, 1)}
                isDragging={subDragId === s.id}
                isDragOver={subDragOverId === s.id && subDragId !== s.id}
                onDragStart={() => setSubDragId(s.id)}
                onDragOver={(e) => { e.preventDefault(); if (subDragOverId !== s.id) setSubDragOverId(s.id); }}
                onDrop={(e) => { e.preventDefault(); handleSubDrop(s.id); }}
                onDragEnd={() => { setSubDragId(null); setSubDragOverId(null); }}
                onChange={(patch) => onSubChange(s.id, patch)}
                onSave={() => onSubSave(s)}
                onDelete={() => onSubDelete(s.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SubRow({ sub, isDragging, isDragOver, canMoveUp, canMoveDown, onMoveUp, onMoveDown, onDragStart, onDragOver, onDrop, onDragEnd, onChange, onSave, onDelete }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const doSave = async () => {
    setSaving(true);
    try { await onSave(); setSaved(true); setTimeout(() => setSaved(false), 1500); }
    finally { setSaving(false); }
  };
  return (
    <div
      className={`bg-[#FBF7EE] border p-4 transition-all ${
        isDragOver ? 'border-[#B08834] border-2' : 'border-[#1F1F1F]/10'
      } ${isDragging ? 'opacity-40' : ''}`}
      data-testid={`cms-sub-${sub.id}`}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
        <div className="md:col-span-1 flex md:flex-col md:items-center gap-1 pt-1 md:pt-6">
          <span
            draggable
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            className="cursor-grab active:cursor-grabbing text-[#5A5145]/50 hover:text-[#1F1F1F] hidden md:inline-flex"
            title="Sürükleyerek sırala"
            data-testid={`cms-sub-drag-${sub.id}`}
          >
            <GripVertical className="w-4 h-4" />
          </span>
          <div className="flex md:flex-col md:gap-0 gap-1">
            <button
              onClick={onMoveUp}
              disabled={!canMoveUp}
              className="text-[#5A5145] disabled:opacity-30 p-0.5"
              data-testid={`cms-sub-up-${sub.id}`}
            >
              <ArrowUp className="w-3 h-3" />
            </button>
            <button
              onClick={onMoveDown}
              disabled={!canMoveDown}
              className="text-[#5A5145] disabled:opacity-30 p-0.5"
              data-testid={`cms-sub-down-${sub.id}`}
            >
              <ArrowDown className="w-3 h-3" />
            </button>
          </div>
        </div>
        <div className="md:col-span-2">
          <ImageField
            label="Görsel"
            compact
            value={sub.image_url || ''}
            onChange={(v) => onChange({ image_url: v })}
            testId={`cms-sub-image-${sub.id}`}
          />
        </div>
        <div className="md:col-span-3">
          <Field label="Ad" value={sub.name} onChange={(v) => onChange({ name: v })} testId={`cms-sub-name-${sub.id}`} />
        </div>
        <div className="md:col-span-4">
          <Field label="Açıklama" value={sub.description || ''} onChange={(v) => onChange({ description: v })} testId={`cms-sub-description-${sub.id}`} />
        </div>
        <div className="md:col-span-2 flex flex-col gap-2 pt-5">
          <button
            onClick={doSave}
            disabled={saving}
            className="inline-flex items-center justify-center gap-1 bg-[#1F1F1F] hover:bg-[#B03A2E] text-[#FBF7EE] px-3 py-2 text-[11px] tracking-[0.18em] disabled:opacity-60"
            data-testid={`cms-sub-save-${sub.id}`}
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            {saved ? 'Kaydedildi' : 'Kaydet'}
          </button>
          <button
            onClick={onDelete}
            className="inline-flex items-center justify-center gap-1 text-[#B03A2E] hover:text-[#8A2E22] px-3 py-2 text-[11px] uppercase tracking-[0.18em] border border-[#B03A2E]/30"
            data-testid={`cms-sub-delete-${sub.id}`}
          >
            <Trash2 className="w-3 h-3" />
            Sil
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, multiline, testId }) {
  return (
    <label className="block">
      <span className="eyebrow text-[#5A5145] block mb-1.5">{label}</span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full bg-white border border-[#1F1F1F]/15 px-3 py-2 text-sm font-body outline-none focus:border-[#B08834]"
          data-testid={testId}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-white border border-[#1F1F1F]/15 px-3 py-2 text-sm font-body outline-none focus:border-[#B08834]"
          data-testid={testId}
        />
      )}
    </label>
  );
}

function ColorField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="eyebrow text-[#5A5145] block mb-1.5">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-9 border border-[#1F1F1F]/15 cursor-pointer bg-white"
          data-testid={`${testId}-picker`}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-white border border-[#1F1F1F]/15 px-3 py-2 text-sm font-mono outline-none focus:border-[#B08834]"
          data-testid={testId}
        />
      </div>
    </label>
  );
}

function ImageField({ label, value, onChange, testId, compact }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/horeca/admin/upload`, fd, {
        params: { token: ADMIN_TOKEN },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data?.url || '');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Yükleme başarısız.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="block">
      <span className="eyebrow text-[#5A5145] block mb-1.5">{label}</span>
      <div className={`flex ${compact ? 'flex-col' : 'items-start'} gap-3`}>
        <div className={`${compact ? 'w-full aspect-square' : 'w-24 h-24'} bg-[#F3EEE2] border border-[#1F1F1F]/10 flex items-center justify-center overflow-hidden shrink-0`}>
          {value ? (
            <img src={resolveImg(value)} alt="" className="w-full h-full object-contain" />
          ) : (
            <ImageIcon className="w-6 h-6 text-[#5A5145]/40" />
          )}
        </div>
        <div className={`flex-1 ${compact ? 'w-full' : ''} space-y-2`}>
          {!compact && (
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Görsel URL (dış link) veya yükleme yapın"
              className="w-full bg-white border border-[#1F1F1F]/15 px-3 py-2 text-sm font-body outline-none focus:border-[#B08834]"
              data-testid={testId}
            />
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 bg-[#B08834]/15 hover:bg-[#B08834]/25 text-[#1F1F1F] px-3 py-1.5 text-[11px] tracking-[0.18em] disabled:opacity-60"
              data-testid={`${testId}-upload`}
            >
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {uploading ? 'Yükleniyor…' : 'Görsel Yükle'}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="text-[11px] text-[#B03A2E] hover:text-[#8A2E22] uppercase tracking-[0.18em]"
                data-testid={`${testId}-clear`}
              >
                Kaldır
              </button>
            )}
          </div>
          {err && <div className="text-[11px] text-[#B03A2E]">{err}</div>}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => doUpload(e.target.files?.[0])}
          />
        </div>
      </div>
    </div>
  );
}
